// productsApi.ts
import { baseApi } from "@shared/api/baseApi"; // Import shared baseApi

interface Product {
  id: string;
  name: string;
  iconUrl: string;
  price: number;
  pricePyl: number;
  type: string;
  position: number;
  isAvailable: boolean;
  isPinned: boolean;
  rarity: string; // Added rarity field
}

interface ProductsResponse {
  items: Product[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

interface ProductsQueryParams {
  page: number; 
  pageSize: number; 
  nameFilter?: string; 
  minPrice?: number; 
  maxPrice?: number; 
  types?: string[]; 
  sortBy?: string; 
  sortDesc?: boolean;
}

interface CurrencyConversion {
  id: string;
  fromCurrency: string;
  fromAmount: number;
  toCurrency: string;
  toAmount: number;
  paymentLink?: string;
}

interface CurrencyConversionsResponse {
  currencyConversions: CurrencyConversion[];
}

// Интерфейс для запроса на покупку продукта
interface PurchaseProductRequest {
  productId: string;
}

// Интерфейс для успешного ответа от API
interface PurchaseProductResponse {
  success: boolean;
  message: string;
  transactionId: string;
  remainingBalance: string;
  tradeOfferId: string;
}

// Интерфейс для запроса на закрепление продукта
interface PinProductRequest {
  productId: string;
  position: number;
}

// Интерфейс для успешного ответа на закрепление/открепление
interface PinUnpinResponse {
  success: boolean;
  message: string;
}

interface SubscriptionResponse {
  PaymentLink: string;
}

interface VerifyEmailRequest {
  email: string;
  redirectUrl?: string;
}

interface ConfirmEmailRequest {
  code: string;
  email: string;
}

interface EmailResponse {
  success: boolean;
  message: string;
}

export const productsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<ProductsResponse, ProductsQueryParams>({
      query: (params) => ({
        url: "shop/archive",
        params,
      }),
    }),
    
    getTopProducts: builder.query<ProductsResponse, void>({
      query: () => "shop/daily",
    }),

    getCurrencyConversions: builder.query<CurrencyConversionsResponse, void>({
      query: () => "currency/conversions",
    }),

    // Мутация для покупки товара
    purchaseProduct: builder.mutation<
      PurchaseProductResponse,
      PurchaseProductRequest
    >({
      query: (data) => ({
        url: "shop/purchase",
        method: "POST",
        body: data,
      }),
    }),

    // Новая мутация для закрепления товара
    pinProduct: builder.mutation<PinUnpinResponse, PinProductRequest>({
      query: (data) => ({
        url: "shop/pin",
        method: "POST",
        body: data,
      }),
    }),

    // Новая мутация для открепления товара
    unpinProduct: builder.mutation<PinUnpinResponse, void>({
      query: () => ({
        url: "shop/unpin",
        method: "POST",
      }),
    }),

    getSubscriptions: builder.query<SubscriptionResponse, void>({
      query: () => "subscription/subscriptions",
    }),

    verifyEmail: builder.mutation<EmailResponse, VerifyEmailRequest>({
      query: (data) => ({
        url: "auth/verify-email",
        method: "POST",
        body: {
          email: data.email,
        },
      }),
    }),

    confirmEmail: builder.mutation<EmailResponse, ConfirmEmailRequest>({
      query: (data) => ({
        url: "auth/confirm-email",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetTopProductsQuery,
  useGetCurrencyConversionsQuery,
  usePurchaseProductMutation,
  usePinProductMutation,    // Новый хук для закрепления товаров
  useUnpinProductMutation,  // Новый хук для открепления товаров
  useGetSubscriptionsQuery, // Добавляем новый хук
  useVerifyEmailMutation,
  useConfirmEmailMutation,
} = productsApi;
